import { useCallback, useRef, useState } from 'react';
import { Upload, FileSpreadsheet, FileText, Loader2, AlertTriangle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import axiosInstance from '@/lib/api/axios-instance';
import { tradeService } from '../services/trade-service';
import type { ProformaExtraction } from '../types/trade.types';

/**
 * Zona de carga de la proforma del proveedor.
 *
 * Acepta arrastrar o elegir. Excel se parsea sin IA (tiene estructura y un
 * parser no alucina); PDF e imagen van al modelo.
 *
 * Lo que se sube NO se importa: devuelve una propuesta que la persona revisa.
 */

const ACCEPT = '.pdf,.xlsx,.xls,.png,.jpg,.jpeg,.webp';
const MAX_MB = 20;

export interface ProformaUploadProps {
  /**
   * Se llama con lo extraído **y con el archivo original**.
   *
   * El archivo viaja porque al confirmar se guarda como documento de la orden: se
   * importan 520 líneas y sin el original no hay con qué defenderse si el cliente
   * reclama un precio tres meses después.
   */
  onExtracted: (extraction: ProformaExtraction, file: File) => void;
  className?: string;
  /**
   * `'button'` abre el explorador de archivos con **un solo click**.
   *
   * Antes había que apretar "Subir proforma" y después "Elegir archivo": dos
   * clicks para una sola intención. El botón sigue aceptando que le arrastres el
   * archivo encima, así que no se pierde el drag & drop.
   */
  mode?: 'dropzone' | 'button';
}

export function ProformaUpload({ onExtracted, className, mode = 'dropzone' }: ProformaUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);

      if (file.size > MAX_MB * 1024 * 1024) {
        setError(`El archivo pesa más de ${MAX_MB} MB.`);
        return;
      }

      setBusy(true);
      setFileName(file.name);
      try {
        const extraction = await tradeService.extractProforma(file);
        onExtracted(extraction, file);
      } catch (err) {
        setError(
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
            'No se pudo leer la proforma.'
        );
      } finally {
        setBusy(false);
      }
    },
    [onExtracted]
  );

  /** El input vive fuera del `return` porque los dos modos lo comparten. */
  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPT}
      className="sr-only"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) void handleFile(file);
        // Permite volver a elegir el mismo archivo.
        e.target.value = '';
      }}
    />
  );

  if (mode === 'button') {
    return (
      <div className={cn('inline-flex flex-col items-start gap-1.5', className)}>
        {fileInput}
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!busy) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const file = e.dataTransfer.files[0];
            if (file && !busy) void handleFile(file);
          }}
          className={cn('gap-1.5', dragging && 'border-prixia-blue bg-prixia-sky/10')}
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Leyendo {fileName}…
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              Subir proforma
            </>
          )}
        </Button>

        {busy ? (
          <p className="text-[11px] text-muted-foreground">
            Un PDF de varias páginas puede tardar medio minuto.
          </p>
        ) : (
          <TemplateLink />
        )}
        {error && (
          <p className="flex max-w-md items-start gap-1.5 rounded-md border border-bloqueo-borde bg-bloqueo-suave px-3 py-2 text-xs text-bloqueo">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const file = e.dataTransfer.files[0];
          if (file && !busy) void handleFile(file);
        }}
        className={cn(
          'relative rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors',
          dragging ? 'border-prixia-blue bg-prixia-sky/10' : 'border-border',
          busy && 'opacity-70'
        )}
      >
        {fileInput}

        {busy ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-7 w-7 animate-spin text-prixia-blue" />
            <p className="text-sm font-medium">Leyendo {fileName}…</p>
            <p className="text-xs text-muted-foreground">
              Un PDF de varias páginas puede tardar medio minuto.
            </p>
          </div>
        ) : (
          <>
            <Upload className="mx-auto h-8 w-8 text-prixia-silver" />
            <p className="mt-2 text-sm font-medium">Arrastrá la proforma del proveedor</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              PDF, Excel o una foto · hasta {MAX_MB} MB
            </p>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => inputRef.current?.click()}
            >
              Elegir archivo
            </Button>

            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <FileSpreadsheet className="h-3 w-3" />
                Excel se lee sin IA
              </span>
              <span className="inline-flex items-center gap-1">
                <FileText className="h-3 w-3" />
                PDF e imagen usan IA
              </span>
              <span className="inline-flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                Siempre revisás antes de importar
              </span>
            </div>
          </>
        )}
      </div>

      {error && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md border border-bloqueo-borde bg-bloqueo-suave px-3 py-2 text-xs text-bloqueo">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Descarga la plantilla de proforma.
 *
 * Existe porque probando el flujo real apareció el hueco: una proforma sin
 * columnas de largo, ancho y alto entra bien, pero después los productos **no se
 * pueden estibar** — el armado 3D no puede ubicar una caja sin dimensiones y el
 * prorrateo por volumen daría cero. Antes había que adivinar qué columnas hacían
 * falta.
 *
 * Baja por el cliente HTTP y no con un `<a href>` porque el endpoint pide el
 * token, y un enlace directo no lleva la cabecera de autorización.
 */
function TemplateLink() {
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const response = await axiosInstance.get('/api/trade/proforma/plantilla', {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'plantilla-proforma.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void download()}
      disabled={busy}
      className="inline-flex items-center gap-1 text-[11px] text-muted-foreground underline decoration-dotted underline-offset-2 transition-colors hover:text-foreground disabled:opacity-50"
    >
      {busy ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <FileSpreadsheet className="h-3 w-3" />
      )}
      Descargar la plantilla de Excel
    </button>
  );
}
